import React, { useState, useEffect, useRef } from 'react';
import { ShieldCheck, Loader2, AlertTriangle, Smartphone } from 'lucide-react';
import { getMfaFactors, challengeAndVerify, addTrustedDevice } from '../lib/mfaService';
import { Header } from './Header';

interface MFAChallengeProps {
  userId: string;
  onVerified: () => void;
  onCancel: () => void;
}

export function MFAChallenge({ userId, onVerified, onCancel }: MFAChallengeProps) {
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [factorId, setFactorId] = useState<string | null>(null);
  const [trustDevice, setTrustDevice] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    // Get the first verified TOTP factor
    (async () => {
      try {
        const factors = await getMfaFactors();
        const totpFactor = factors.totp.find(f => f.status === 'verified');
        if (totpFactor) {
          setFactorId(totpFactor.id);
        } else {
          setError('No verified 2FA factor found. Please contact support.');
        }
      } catch (err) {
        console.error('Error loading MFA factors:', err);
        setError('Failed to load 2FA settings.');
      }
    })();

    // Auto-focus the input
    setTimeout(() => inputRef.current?.focus(), 100);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!factorId || code.length !== 6) return;

    setIsLoading(true);
    setError('');

    try {
      // Add timeout to prevent infinite hang
      const timeoutPromise = new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error('Verification timed out. Please try again.')), 10000)
      );

      await Promise.race([
        challengeAndVerify(factorId, code),
        timeoutPromise,
      ]);

      // If trust device is checked, save it in background (don't await)
      if (trustDevice) {
        addTrustedDevice(userId).catch(err => {
          console.error('Failed to save trusted device:', err);
        });
      }

      onVerified();
    } catch (err: unknown) {
      console.error('MFA verification error:', err);
      const message = err instanceof Error ? err.message : 'Verification failed';
      if (message.includes('Invalid TOTP') || message.includes('invalid')) {
        setError('Invalid code. Please check your authenticator app and try again.');
      } else {
        setError(message);
      }
      setCode('');
      inputRef.current?.focus();
    } finally {
      setIsLoading(false);
    }
  };

  const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
    setCode(value);
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-100">
      <Header />
      
      <div className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="max-w-md w-full space-y-8 bg-white p-8 rounded-lg shadow-md">
          {/* Header */}
          <div className="flex flex-col items-center space-y-4">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
              <ShieldCheck className="w-9 h-9 text-green-600" />
            </div>
            <div className="text-center">
              <h2 className="text-2xl font-bold text-gray-900">
                Two-Factor Authentication
              </h2>
              <p className="mt-2 text-sm text-gray-600">
                Enter the 6-digit code from your authenticator app to continue.
              </p>
            </div>
          </div>

          {/* Error */}
          {error && (
            <div className="rounded-md bg-red-50 border border-red-200 p-4">
              <div className="flex items-start">
                <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5 mr-3 flex-shrink-0" />
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Code Input */}
            <div>
              <label htmlFor="mfa-code" className="block text-sm font-medium text-gray-700 mb-2">
                Verification Code
              </label>
              <input
                ref={inputRef}
                id="mfa-code"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                value={code}
                onChange={handleCodeChange}
                placeholder="000000"
                maxLength={6}
                disabled={isLoading || !factorId}
                className="block w-full px-4 py-3 text-center text-2xl font-mono tracking-[0.5em] border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-100"
              />
            </div>

            {/* Trust Device */}
            <div className="flex items-center">
              <input
                id="trust-device"
                type="checkbox"
                checked={trustDevice}
                onChange={(e) => setTrustDevice(e.target.checked)}
                className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
              />
              <label htmlFor="trust-device" className="ml-3 flex items-center text-sm text-gray-600">
                <Smartphone className="w-4 h-4 mr-1.5 text-gray-400" />
                Remember this device for 30 days
              </label>
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={isLoading || code.length !== 6 || !factorId}
              className="w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isLoading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                'Verify & Continue'
              )}
            </button>

            {/* Cancel */}
            <div className="text-center">
              <button
                type="button"
                onClick={onCancel}
                className="text-sm text-gray-500 hover:text-gray-700 underline"
              >
                Sign out
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
